"use client"

import * as React from "react"
import { Calendar as CalendarIcon } from "lucide-react"
import { format } from "date-fns"
import { mn } from 'date-fns/locale'

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area"
import { Label } from "../ui/label"
import { getBackgroundColorClass } from "@/utils/util"

interface DateTimePickerProps {
  date?: Date | null
  setDate: (date: Date | undefined) => void
  label?: string
  required?: boolean
  container?: string
  className?: string
  placeholder?: string
  color?: string
}

export function DateTimePicker({ date, setDate, label, required = false, container, className, placeholder = "Огноо болон цаг сонгоно уу", color }: DateTimePickerProps) {

  const backgroundColorClass = getBackgroundColorClass(color)

  const [isOpen, setIsOpen] = React.useState(false)
  const hours = Array.from({ length: 24 }, (_, i) => i)

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      if (date) {
        const newDate = new Date(selectedDate)
        newDate.setHours(date.getHours())
        newDate.setMinutes(date.getMinutes())
        setDate(newDate)
      } else {
        const newDate = new Date(selectedDate)
        newDate.setHours(9)
        newDate.setMinutes(0)
        setDate(newDate)
      }
    }
  }

  const handleTimeChange = (
    type: "hour" | "minute",
    value: string
  ) => {
    if (date) {
      const newDate = new Date(date)
      if (type === "hour") {
        newDate.setHours(parseInt(value))
      } else if (type === "minute") {
        newDate.setMinutes(parseInt(value))
      }
      setDate(newDate)
    }
  }

  return (
    <div className={`grid w-full items-center gap-1.5 ${container || ''}`}>
      {label && (
        <Label className="mb-1">
          {label}
          {required && (<span className='text-red-500 -ml-1'>*</span>)}
        </Label>
      )}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-between text-left font-normal",
              !date && "text-muted-foreground",
              className
            )}
          >
            {date ? (
              format(date, "yyyy оны MM сарын dd, HH:mm", { locale: mn })
            ) : (
              <span>{placeholder}</span>
            )}
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <div className="sm:flex">
            <Calendar
              mode="single"
              selected={date || undefined}
              onSelect={handleDateSelect}
              initialFocus
              color={color}
            />
            <div className="flex flex-col sm:flex-row sm:h-[300px] divide-y sm:divide-y-0 sm:divide-x">
              {/* Hours */}
              <ScrollArea className="w-64 sm:w-auto">
                <div className="flex sm:flex-col p-2">
                  {hours.map((hour) => {
                    const isSelected = date && date.getHours() === hour
                    return (
                      <Button
                        key={hour}
                        size="icon"
                        variant={isSelected ? "default" : "ghost"}
                        className={cn("sm:w-full shrink-0 aspect-square", isSelected && backgroundColorClass)}
                        onClick={() => handleTimeChange("hour", hour.toString())}
                      >
                        {hour.toString().padStart(2, '0')}
                      </Button>
                    )
                  })}
                </div>
                <ScrollBar orientation="horizontal" className="sm:hidden" />
              </ScrollArea>
              {/* Minutes */}
              <ScrollArea className="w-64 sm:w-auto">
                <div className="flex sm:flex-col p-2">
                  {Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => {
                    const isSelected = date && date.getMinutes() === minute
                    return (
                      <Button
                        key={minute}
                        size="icon"
                        variant={isSelected ? "default" : "ghost"}
                        className={cn("sm:w-full shrink-0 aspect-square", isSelected && backgroundColorClass)}
                        onClick={() => handleTimeChange("minute", minute.toString())}
                      >
                        {minute.toString().padStart(2, '0')}
                      </Button>
                    )
                  })}
                </div>
                <ScrollBar orientation="horizontal" className="sm:hidden" />
              </ScrollArea>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}